import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  CheckCircle2,
  XCircle,
  Clock,
  Bell,
  Users,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { ComprehensiveScenario, ScenarioApproval } from '../../types/scenarios';
import { formatDate } from '../../lib/utils';
import { CreateActionModal } from '../actions/CreateActionModal';
import { useActions } from '../../contexts/ActionsContext';

interface ViewApproversModalProps {
  open: boolean;
  onClose: () => void;
  scenario: ComprehensiveScenario;
}

const TOTAL_STAGES = 2; // Matches DEMO_WORKFLOW_STAGES — Stage 1: CFO, Stage 2: CEO

export const ViewApproversModal: React.FC<ViewApproversModalProps> = ({
  open,
  onClose,
  scenario,
}) => {
  const [nudgeApprover, setNudgeApprover] = useState<ScenarioApproval | null>(null);
  const { createAction } = useActions();

  const currentStage = scenario.currentStageOrder ?? 1;
  const chain = scenario.approvalChain ?? [];

  // Count how many approvers are required at the current stage
  // In our workflow each stage has exactly 1 required approver
  const requiredApprovers = 1;

  // Count how many at current stage have already actioned (approved or rejected)
  const currentStageApprovers = chain.filter((a) => a.order === currentStage);
  const actionedCount = currentStageApprovers.filter(
    (a) => a.status === 'approved' || a.status === 'rejected',
  ).length;
  const pendingCount = requiredApprovers - actionedCount;

  // All approvers in the chain — show all stages for context
  // but visually group by stage
  const stage1Approvers = chain.filter((a) => a.order === 1);
  const stage2Approvers = chain.filter((a) => a.order === 2);

  const getStatusIcon = (status: ScenarioApproval['status']) => {
    if (status === 'approved') return <CheckCircle2 className="h-4 w-4 text-[var(--accent-success-500)]" />;
    if (status === 'rejected') return <XCircle className="h-4 w-4 text-[var(--accent-destructive-500)]" />;
    return <Clock className="h-4 w-4 text-[var(--accent-warning-500)]" />;
  };

  const getStatusLabel = (status: ScenarioApproval['status']) => {
    if (status === 'approved') return 'Approved';
    if (status === 'rejected') return 'Rejected';
    if (status === 'changes-requested') return 'Changes Requested';
    return 'Awaiting Action';
  };

  const getStatusBadgeClass = (status: ScenarioApproval['status']) => {
    if (status === 'approved') return 'bg-[var(--accent-success-50)] text-[var(--accent-success-900)] border-[var(--accent-success-300)]';
    if (status === 'rejected') return 'bg-[var(--accent-destructive-50)] text-[var(--accent-destructive-900)] border-[var(--accent-destructive-300)]';
    return 'bg-[var(--accent-warning-50)] text-[var(--accent-warning-900)] border-[var(--accent-warning-300)]';
  };

  const renderApproverRow = (approver: ScenarioApproval, isCurrentStage: boolean) => {
    const isPending = approver.status === 'pending';
    const isActioned = approver.status === 'approved' || approver.status === 'rejected';

    return (
      <div
        key={approver.id}
        className={cn(
          'flex items-center justify-between p-3 rounded-lg border',
          isActioned
            ? 'bg-gray-50 border-gray-100'
            : isCurrentStage
              ? 'bg-white border-[var(--accent-warning-200)]'
              : 'bg-gray-50 border-gray-100 opacity-60',
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          {getStatusIcon(approver.status)}
          <div className="min-w-0">
            <div className="text-sm font-medium text-gray-900">{approver.userName}</div>
            <div className="text-xs text-gray-500">{approver.userRole}</div>
            {isActioned && approver.timestamp && (
              <div className="text-xs text-gray-400 mt-0.5">
                {getStatusLabel(approver.status)} on {formatDate(approver.timestamp)}
              </div>
            )}
            {isActioned && approver.comments && (
              <div className="text-xs text-gray-500 mt-1 italic line-clamp-2">
                "{approver.comments}"
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0 ml-3">
          {isPending && isCurrentStage ? (
            <Button
              size="sm"
              variant="outline"
              className="gap-1.5 text-xs"
              onClick={() => setNudgeApprover(approver)}
            >
              <Bell className="h-3 w-3" />
              Nudge
            </Button>
          ) : (
            <Badge
              variant="outline"
              className={cn('text-xs font-medium', getStatusBadgeClass(approver.status))}
            >
              {getStatusLabel(approver.status)}
            </Badge>
          )}
        </div>
      </div>
    );
  };

  const nudgeActionTitle = nudgeApprover
    ? `Approval requested: ${scenario.name}`
    : '';
  const nudgeActionDescription = nudgeApprover
    ? `Hi ${nudgeApprover.userName.split(' ')[0]}, could you please review and action the approval for scenario "${scenario.name}"? It is currently at Stage ${currentStage} of ${TOTAL_STAGES} and awaiting your sign-off. Thank you.`
    : '';

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5 text-[var(--accent-warning-500)]" />
              Approval Status
            </DialogTitle>
          </DialogHeader>

          {/* Scenario name */}
          <p className="text-sm text-gray-500 -mt-1 truncate">{scenario.name}</p>

          {/* Status summary banner */}
          <div className="flex items-center gap-3 p-3 bg-[var(--accent-warning-50)] border border-[var(--accent-warning-200)] rounded-lg">
            <Clock className="h-4 w-4 text-[var(--accent-warning-600)] flex-shrink-0" />
            <div className="text-sm">
              <span className="font-semibold text-[var(--accent-warning-900)]">
                Awaiting Approval
              </span>
              <span className="text-[var(--accent-warning-700)]">
                {' '}— Stage {currentStage} of {TOTAL_STAGES}
                {pendingCount > 0 && ` · ${pendingCount} approval${pendingCount !== 1 ? 's' : ''} still required`}
                {pendingCount === 0 && ' · All approvals received'}
              </span>
            </div>
          </div>

          {/* Stage 1 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Stage 1 — Finance Review
              </span>
              {currentStage === 1 && (
                <Badge variant="outline" className="text-xs bg-[var(--accent-warning-50)] text-[var(--accent-warning-700)] border-[var(--accent-warning-300)]">
                  Current Stage
                </Badge>
              )}
              {currentStage > 1 && (
                <Badge variant="outline" className="text-xs bg-[var(--accent-success-50)] text-[var(--accent-success-700)] border-[var(--accent-success-300)]">
                  Complete
                </Badge>
              )}
            </div>
            {stage1Approvers.length > 0 ? (
              stage1Approvers.map((a) => renderApproverRow(a, currentStage === 1))
            ) : (
              <div className="text-xs text-gray-400 italic px-3">No approver assigned</div>
            )}
          </div>

          {/* Stage 2 */}
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
                Stage 2 — CEO Sign-off
              </span>
              {currentStage === 2 && (
                <Badge variant="outline" className="text-xs bg-[var(--accent-warning-50)] text-[var(--accent-warning-700)] border-[var(--accent-warning-300)]">
                  Current Stage
                </Badge>
              )}
              {currentStage < 2 && (
                <Badge variant="outline" className="text-xs bg-gray-100 text-gray-500 border-gray-200">
                  Pending
                </Badge>
              )}
            </div>
            {stage2Approvers.length > 0 ? (
              stage2Approvers.map((a) => renderApproverRow(a, currentStage === 2))
            ) : currentStage >= 2 ? (
              <div className="p-3 rounded-lg border border-[var(--accent-warning-200)] bg-white">
                <div className="flex items-center gap-3">
                  <Clock className="h-4 w-4 text-[var(--accent-warning-500)]" />
                  <div>
                    <div className="text-sm font-medium text-gray-900">Catherine Hughes</div>
                    <div className="text-xs text-gray-500">Chief Executive Officer</div>
                  </div>
                  <div className="ml-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1.5 text-xs"
                      onClick={() =>
                        setNudgeApprover({
                          id: 'stage2-pending',
                          userId: '1',
                          approverId: '1',
                          userName: 'Catherine Hughes',
                          userRole: 'Chief Executive Officer',
                          status: 'pending',
                          timestamp: new Date(),
                          order: 2,
                        })
                      }
                    >
                      <Bell className="h-3 w-3" />
                      Nudge
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-xs text-gray-400 italic px-3">Will be assigned when Stage 1 is complete</div>
            )}
          </div>

          <div className="flex justify-end pt-2 border-t border-gray-100">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pre-filled nudge action modal */}
      {nudgeApprover && (
        <CreateActionModal
          open={!!nudgeApprover}
          onClose={() => setNudgeApprover(null)}
          onConfirm={(payload) => {
            createAction(payload);
            setNudgeApprover(null);
          }}
          prefill={{
            title: nudgeActionTitle,
            description: nudgeActionDescription,
            assignedTo: nudgeApprover.userId,
          }}
        />
      )}
    </>
  );
};
